import React, { useState } from "react";
import { Modal, ModalBody, ModalHeader, ModalFooter, Button } from "reactstrap";
import { useEditor } from "./EditorContext";

// ============================================================================
// PILOTO - Assistente de IA (STUB)
// ----------------------------------------------------------------------------
// Este componente é um stub 100% frontend. Não faz chamadas de rede — retorna
// HTML pré-definido baseado em palavras-chave no prompt. Serve apenas para
// validar a UX antes de subir o backend real.
//
// COMO REVERTER:
//   1. Em EditorTopbar.jsx, trocar `const USE_AI_ASSISTANT = true;` para
//      `false` (o botão some da topbar).
//   2. Ou remover o import + renderização de <AIAssistant /> no EditorTopbar.
//   3. Deletar este arquivo — sem outros efeitos colaterais.
//
// COMO EVOLUIR PARA O REAL:
//   Trocar a função `mockGenerate` por um fetch("/ai/generate-block", { prompt })
//   que retorna o HTML gerado pelo LLM no backend. Manter a lógica de
//   editor.addComponents(html) e o modal.
// ============================================================================

const EXAMPLES = [
  { label: "Hero", prompt: "Uma seção hero com título grande, subtítulo e botão de call-to-action" },
  { label: "CTA", prompt: "Uma seção de call-to-action centralizada com botão roxo" },
  { label: "Card", prompt: "Um card com imagem no topo, título, descrição e botão" },
  { label: "Rodapé", prompt: "Um rodapé simples com links e copyright" },
];

// Stub — retorna HTML pré-definido baseado em palavras-chave no prompt.
// Simula latência de rede com setTimeout.
const mockGenerate = (prompt) =>
  new Promise((resolve) => {
    const p = prompt.toLowerCase();
    let html = "";

    if (p.includes("hero") || p.includes("banner")) {
      html = `
        <section style="padding: 60px 24px; text-align: center; background: linear-gradient(135deg, #040050 0%, #0a007a 100%); color: #fff;">
          <h1 style="font-size: 42px; font-weight: 700; margin: 0 0 12px;">Bem-vindo ao Evento</h1>
          <p style="font-size: 18px; opacity: 0.9; margin: 0 0 24px;">Descubra experiências únicas com a gente.</p>
          <a href="#" style="display: inline-block; padding: 14px 32px; background: #fff; color: #040050; border-radius: 6px; text-decoration: none; font-weight: 600;">Saber mais</a>
        </section>
      `;
    } else if (p.includes("cta") || p.includes("call")) {
      html = `
        <section style="padding: 48px 24px; text-align: center; background: #f7f8fa;">
          <h2 style="font-size: 28px; margin: 0 0 8px;">Pronto para começar?</h2>
          <p style="color: #6b7280; margin: 0 0 24px;">Junte-se a milhares de participantes.</p>
          <a href="#" style="display: inline-block; padding: 12px 28px; background: #040050; color: #fff; border-radius: 6px; text-decoration: none; font-weight: 600;">Inscreva-se agora</a>
        </section>
      `;
    } else if (p.includes("card")) {
      html = `
        <div style="max-width: 320px; margin: 24px auto; border: 1px solid #e6e8eb; border-radius: 12px; overflow: hidden; background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.04);">
          <img src="https://via.placeholder.com/320x180/040050/ffffff?text=Imagem" alt="" style="width: 100%; display: block;" />
          <div style="padding: 20px;">
            <h3 style="font-size: 18px; margin: 0 0 8px;">Título do Card</h3>
            <p style="color: #6b7280; font-size: 14px; margin: 0 0 16px;">Descrição breve do conteúdo do card.</p>
            <a href="#" style="color: #040050; text-decoration: none; font-weight: 600;">Ver mais →</a>
          </div>
        </div>
      `;
    } else if (p.includes("rodapé") || p.includes("footer")) {
      html = `
        <footer style="padding: 32px 24px; background: #1f2937; color: #d1d5db; text-align: center;">
          <div style="margin-bottom: 12px;">
            <a href="#" style="color: #d1d5db; margin: 0 12px; text-decoration: none;">Sobre</a>
            <a href="#" style="color: #d1d5db; margin: 0 12px; text-decoration: none;">Contato</a>
            <a href="#" style="color: #d1d5db; margin: 0 12px; text-decoration: none;">Privacidade</a>
          </div>
          <p style="font-size: 12px; margin: 0; opacity: 0.7;">© 2026 Inteegra. Todos os direitos reservados.</p>
        </footer>
      `;
    } else if (p.includes("botão") || p.includes("botao") || p.includes("button")) {
      html = `<a href="#" style="display: inline-block; padding: 12px 28px; background: #040050; color: #fff; border-radius: 6px; text-decoration: none; font-weight: 600; margin: 12px;">Clique aqui</a>`;
    } else if (p.includes("título") || p.includes("titulo") || p.includes("heading")) {
      html = `<h1 style="font-size: 36px; margin: 24px; color: #1f2937;">Novo título</h1>`;
    } else {
      // fallback genérico
      html = `
        <section style="padding: 32px 24px; background: #fff; border: 1px dashed #d0d5da; border-radius: 8px; margin: 12px;">
          <h2 style="font-size: 22px; margin: 0 0 8px;">Bloco gerado (piloto)</h2>
          <p style="color: #6b7280; margin: 0;">Prompt: "${prompt}"</p>
          <p style="color: #9ca3af; font-size: 12px; margin-top: 12px;">
            <em>Este é um stub. Palavras-chave suportadas: hero, cta, card, rodapé, botão, título.</em>
          </p>
        </section>
      `;
    }

    setTimeout(() => resolve(html), 800);
  });

const AIAssistant = () => {
  const { editor } = useEditor();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);

  const toggle = () => {
    if (loading) return;
    setOpen((v) => !v);
    if (open) setPrompt("");
  };

  const handleGenerate = async () => {
    if (!prompt.trim() || !editor) return;
    setLoading(true);
    try {
      const html = await mockGenerate(prompt);
      editor.addComponents(html);
      setOpen(false);
      setPrompt("");
    } catch (err) {
      console.error("[AIAssistant] erro ao gerar:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="v2-tb-icon v2-tb-ai"
        title="Gerar com IA (piloto)"
        onClick={() => setOpen(true)}
      >
        <i className="fa fa-magic" />
      </button>

      <Modal isOpen={open} toggle={toggle} centered size="md">
        <ModalHeader toggle={toggle}>
          <i className="fa fa-magic" style={{ color: "#040050", marginRight: 8 }} />
          Gerar componente com IA
          <span
            style={{
              marginLeft: 10,
              fontSize: 11,
              background: "#fef3c7",
              color: "#92400e",
              padding: "2px 8px",
              borderRadius: 4,
              fontWeight: 600,
            }}
          >
            PILOTO
          </span>
        </ModalHeader>
        <ModalBody>
          <label
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#6b7280",
              display: "block",
              marginBottom: 6,
            }}
          >
            Descreva o componente que quer gerar
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Ex: Uma seção hero com título 'Bem-vindo', subtítulo curto e botão roxo 'Comece agora'"
            rows={4}
            disabled={loading}
            style={{
              width: "100%",
              padding: 10,
              border: "1px solid #d0d5da",
              borderRadius: 8,
              fontSize: 13,
              resize: "vertical",
              fontFamily: "inherit",
            }}
          />

          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 11, color: "#6b7280", alignSelf: "center" }}>
              Exemplos:
            </span>
            {EXAMPLES.map((ex) => (
              <button
                key={ex.label}
                type="button"
                onClick={() => setPrompt(ex.prompt)}
                disabled={loading}
                style={{
                  padding: "4px 10px",
                  fontSize: 11,
                  background: "#f7f8fa",
                  border: "1px solid #e6e8eb",
                  borderRadius: 12,
                  color: "#1f2937",
                  cursor: "pointer",
                }}
              >
                {ex.label}
              </button>
            ))}
          </div>

          <div
            style={{
              marginTop: 12,
              padding: 10,
              background: "#fffbeb",
              border: "1px solid #fef3c7",
              borderRadius: 8,
              fontSize: 11,
              color: "#92400e",
            }}
          >
            <strong>Modo piloto:</strong> a geração é simulada no frontend com HTML
            pré-definido. Palavras-chave suportadas: hero, cta, card, rodapé, botão,
            título.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={toggle} disabled={loading}>
            Cancelar
          </Button>
          <Button
            style={{ background: "#040050", borderColor: "#040050" }}
            onClick={handleGenerate}
            disabled={loading || !prompt.trim()}
          >
            {loading ? (
              <>
                <i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />
                Gerando...
              </>
            ) : (
              <>
                <i className="fa fa-magic" style={{ marginRight: 6 }} />
                Gerar
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default AIAssistant;
